import React, { useState, useCallback, useEffect, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DineBoard, DineSpecial } from 'api/types';
import DineOpsSpecialAddModal from 'components/modals/modules/dineops-module/DineOpsSpecialAddModal';
import DineOpsSpecialRunModal from 'components/modals/modules/dineops-module/DineOpsSpecialRunModal';

/**
 * MENU-004 — Today's Special (spec 8.8).
 *
 * A mark on dishes that already have a home, never a group of their own. Its
 * own screen because setting the board is not editing the card: the card is
 * organised by course and group, the board by date. Each row is a RUN — dish,
 * where it really lives, the dates, the line to say, and whether it is on,
 * booked or finished. The + opens the whole card, searchable, many at a time.
 */

interface Filters {
  state?: string;
  course?: string;
}

const DineOpsSpecialsScreen: React.FC = () => {
  const [board, setBoard] = useState<DineBoard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({});
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<DineSpecial | null>(null);
  const [removing, setRemoving] = useState<DineSpecial | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try { setBoard(await dineOpsService.getBoard()); }
    catch { setError(true); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const rows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return (board?.runs ?? []).filter((r) =>
      (!filters.state || r.state === filters.state)
      && (!filters.course || r.courseName === filters.course)
      && (!q || r.itemName.toLowerCase().includes(q) || r.itemCode.toLowerCase().includes(q) || (r.note ?? '').toLowerCase().includes(q)));
  }, [board, searchQuery, filters]);

  const courses = useMemo(
    () => Array.from(new Set((board?.runs ?? []).map((r) => r.courseName))).sort(),
    [board],
  );

  const handleExport = useCallback(() => {
    const header = ['Code', 'Dish', 'Category', 'Group', 'Price', 'From', 'To', 'Says', 'Status'];
    const lines = rows.map((r) => [r.itemCode, r.itemName, r.courseName, r.groupName, r.isOpenPrice ? '' : String(r.price), r.fromDate.slice(0, 10), r.toDate.slice(0, 10), r.note ?? '', r.state]);
    const csv = [header, ...lines].map((l) => l.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `todays-special-${board?.businessDate ?? ''}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [rows, board]);

  const handleRemove = useCallback(async () => {
    if (!removing) return;
    try {
      setBoard(await dineOpsService.takeOffBoard(removing.id, removing.rowVersion));
      notificationService.success('Taken off', `'${removing.itemName}' is off the board. It is still on the menu.`);
    } catch (err: any) {
      notificationService.error('Not removed', err?.response?.data?.message ?? 'The run could not be removed.');
    } finally {
      setRemoving(null);
    }
  }, [removing]);

  const actionButtons: ActionButton[] = useMemo(
    () => [
      { id: 'add', icon: <IconSystem.add size={18} />, label: '+', onClick: () => setAdding(true), tooltip: 'Put dishes on the board', variant: 'icon' },
      { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: '', onClick: () => void load(), tooltip: 'Refresh', variant: 'icon' },
      { id: 'export', icon: <IconSystem.download size={18} />, label: '', onClick: handleExport, tooltip: 'Export to CSV', variant: 'icon' },
    ],
    [load, handleExport],
  );

  const badge = (state: string) => {
    const style: React.CSSProperties = {
      fontSize: 11, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap',
      background: state === 'ON' ? 'var(--color-success-bg, #dcfce7)' : state === 'BOOKED' ? 'var(--color-warning-bg, #fef3c7)' : 'var(--bg-muted, #f1f5f9)',
      color: state === 'ON' ? 'var(--color-success, #166534)' : state === 'BOOKED' ? 'var(--color-warning, #92400e)' : 'var(--text-secondary, #64748b)',
    };
    return <span style={style}>{state === 'ON' ? 'On the board' : state === 'BOOKED' ? 'Booked' : 'Finished'}</span>;
  };

  const columns: SyutGridColumn<DineSpecial>[] = useMemo(
    () => [
      { id: 'itemCode', header: 'Code', accessorKey: 'itemCode', enableSorting: true, size: 90 },
      {
        id: 'itemName', header: 'Dish', accessorKey: 'itemName', enableSorting: true, size: 220,
        cell: ({ row }) => (
          <span>
            <strong>{row.original.itemName}</strong>
            {row.original.isSoldOut && <span className="text-danger" style={{ marginLeft: 6, fontSize: 11 }}>sold out</span>}
          </span>
        ),
      },
      {
        id: 'where', header: 'Where it lives', accessorKey: 'groupName', enableSorting: true, size: 200,
        cell: ({ row }) => <span style={{ opacity: 0.75 }}>{row.original.courseName} › {row.original.groupName}</span>,
      },
      {
        id: 'price', header: 'Price', accessorKey: 'price', enableSorting: true, size: 90,
        cell: ({ row }) => (row.original.isOpenPrice ? <span style={{ opacity: 0.6 }}>open priced</span> : row.original.price.toFixed(2)),
      },
      {
        id: 'run', header: 'On the board', accessorKey: 'fromDate', enableSorting: true, size: 190,
        cell: ({ row }) => {
          const f = row.original.fromDate.slice(0, 10); const t = row.original.toDate.slice(0, 10);
          return f === t ? f : `${f} → ${t}`;
        },
      },
      { id: 'note', header: 'Says', accessorKey: 'note', enableSorting: false, size: 180, cell: ({ row }) => row.original.note ?? <span style={{ opacity: 0.5 }}>—</span> },
      { id: 'displayOrder', header: 'Position', accessorKey: 'displayOrder', enableSorting: true, size: 90 },
      { id: 'state', header: 'Status', accessorKey: 'state', enableSorting: true, size: 120, cell: ({ row }) => badge(row.original.state) },
    ],
    [],
  );

  const getRowActions = useCallback(
    (row: DineSpecial) => [
      { id: 'edit', label: 'Change its run', icon: <IconSystem.edit size={16} />, onClick: () => setEditing(row) },
      { id: 'remove', label: 'Take off the board', icon: <IconSystem.trash size={16} />, onClick: () => setRemoving(row), variant: 'danger' as const },
    ],
    [],
  );

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Today's Special"
        description="A mark on dishes that already have a home — never a group of their own"
        icon={<IconSystem.tag size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search the board' }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, tooltip: 'Toggle filters' }}
        actions={actionButtons}
      />

      <div className="module-content">
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            {board && (
              <div style={{ padding: '8px 12px', fontSize: 13, display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'baseline' }}>
                <span><strong>{board.onCount}</strong> on the board for {board.businessDate}{board.bookedCount ? <> · <strong>{board.bookedCount}</strong> booked for later</> : null}</span>
                <span style={{ fontSize: 12, opacity: 0.65 }}>
                  Every dish here keeps its own code, group, price and attributes. What it sells reports under its real group; being on the board is a run of dates.
                </span>
              </div>
            )}
            <SyutGrid
              data={rows}
              columns={columns}
              enableSorting
              enablePagination
              defaultPageSize={50}
              pageSizeOptions={[25, 50, 100]}
              frozenRightColumns={['actions']}
              isLoading={loading}
              rowActions={getRowActions}
              emptyMessage={error ? 'The board could not be loaded.' : 'Nothing on the board. Use + to put dishes on it.'}
            />
          </div>

          <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)}><IconSystem.x size={16} /></button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={[{ value: '', label: 'Any' }, { value: 'ON', label: 'On the board' }, { value: 'BOOKED', label: 'Booked' }, { value: 'FINISHED', label: 'Finished' }]}
                  value={filters.state || ''}
                  onChange={(v) => setFilters((p) => ({ ...p, state: (v as string) || undefined }))}
                  placeholder="Any"
                  size="sm"
                  isClearable
                />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Category"
                  options={[{ value: '', label: 'Any' }, ...courses.map((c) => ({ value: c, label: c }))]}
                  value={filters.course || ''}
                  onChange={(v) => setFilters((p) => ({ ...p, course: (v as string) || undefined }))}
                  placeholder="Any"
                  size="sm"
                  isClearable
                />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => setFilters({})}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </div>
        </div>
      </div>

      {adding && (
        <DineOpsSpecialAddModal
          isOpen
          businessDate={board?.businessDate ?? ''}
          alreadyOn={(board?.runs ?? []).filter((r) => r.state !== 'FINISHED').map((r) => r.itemId)}
          onSaved={(b) => { setBoard(b); setAdding(false); }}
          onClose={() => setAdding(false)}
        />
      )}

      {editing && (
        <DineOpsSpecialRunModal
          isOpen
          run={editing}
          onSaved={(b) => { setBoard(b); setEditing(null); }}
          onClose={() => setEditing(null)}
        />
      )}

      {removing && (
        <ConfirmDialog
          open
          title="Take it off the board?"
          message={`'${removing.itemName}' comes off the board. It stays on the menu, in ${removing.groupName}, at its price.`}
          confirmLabel="Take off"
          cancelLabel="Keep it"
          variant="danger"
          onConfirm={handleRemove}
          onCancel={() => setRemoving(null)}
        />
      )}
    </div>
  );
};

export default DineOpsSpecialsScreen;
